// managementCustomersSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAllCustomers,
  fetchAssignedCustomers,
  addCustomer,
  removeCustomer,
  updateCustomer,
} from "../../pages/setup/components/managementcompanies/services/managementCustomerService";

// Thunk for fetching assigned customers for a given management company
export const fetchAssignedCustomersThunk = createAsyncThunk(
  "managementCustomers/fetchAssignedCustomers",
  async (managementId, thunkAPI) => {
    try {
      const data = await fetchAssignedCustomers(managementId);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Thunk for fetching all customers (for dropdown options)
export const fetchAllCustomersThunk = createAsyncThunk(
  "managementCustomers/fetchAllCustomers",
  async (_, thunkAPI) => {
    try {
      const data = await fetchAllCustomers();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Thunk for adding a customer to a management company
export const addCustomerThunk = createAsyncThunk(
  "managementCustomers/addCustomer",
  async ({ managementId, customerID }, thunkAPI) => {
    try {
      const data = await addCustomer(managementId, customerID);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Thunk for removing a customer from a management company
export const removeCustomerThunk = createAsyncThunk(
  "managementCustomers/removeCustomer",
  async ({ managementId, customerID }, thunkAPI) => {
    try {
      const data = await removeCustomer(managementId, customerID);
      // Return the customerID to update the state accordingly
      return { customerID, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Thunk for updating a customer's information
export const updateCustomerThunk = createAsyncThunk(
  "managementCustomers/updateCustomer",
  async ({ customerId, customerData }, thunkAPI) => {
    try {
      const data = await updateCustomer(customerId, customerData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState = {
  assignedCustomers: [],
  customerOptions: [],
};

const managementCustomersSlice = createSlice({
  name: "managementCustomers",
  initialState,
  reducers: {
    setAssignedCustomers(state, action) {
      state.assignedCustomers = action.payload;
    },
    setCustomerOptions(state, action) {
      state.customerOptions = action.payload;
    },
    updateCustomerInState(state, action) {
      const updatedCustomer = action.payload;
      const index = state.assignedCustomers.findIndex(
        (c) => c.customerID === updatedCustomer.customerID
      );
      if (index !== -1) {
        state.assignedCustomers[index] = updatedCustomer;
      }
    },
    addCustomerToState(state, action) {
      state.assignedCustomers.push(action.payload);
    },
    removeCustomerFromState(state, action) {
      const customerID = action.payload;
      state.assignedCustomers = state.assignedCustomers.filter(
        (c) => c.customerID !== customerID
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssignedCustomersThunk.fulfilled, (state, action) => {
        state.assignedCustomers = action.payload;
      })
      .addCase(fetchAllCustomersThunk.fulfilled, (state, action) => {
        state.customerOptions = action.payload;
      })
      .addCase(addCustomerThunk.fulfilled, (state, action) => {
        state.assignedCustomers.push(action.payload);
      })
      .addCase(removeCustomerThunk.fulfilled, (state, action) => {
        const { customerID } = action.payload;
        state.assignedCustomers = state.assignedCustomers.filter(
          (c) => c.customerID !== customerID
        );
      })
      .addCase(updateCustomerThunk.fulfilled, (state, action) => {
        const updatedCustomer = action.payload;
        const index = state.assignedCustomers.findIndex(
          (c) => c.customerID === updatedCustomer.customerID
        );
        if (index !== -1) {
          state.assignedCustomers[index] = updatedCustomer;
        }
      });
  },
});

export const {
  setAssignedCustomers,
  setCustomerOptions,
  updateCustomerInState,
  addCustomerToState,
  removeCustomerFromState,
} = managementCustomersSlice.actions;

export default managementCustomersSlice.reducer;
